import React from 'react';
import useResizeObserver from 'beautiful-react-hooks/useResizeObserver';
import { FC, ReactNode, RefObject, useEffect, useRef } from 'react';
import { MapProvider, NavigationControl, Map as ReactMap, ScaleControl, useMap } from 'react-map-gl';
import { MAPBOX_TOKEN } from './map-consts';

type MapBaseProps = {
    children?: ReactNode;
    mapId?: string;
    initialViewState?: {
        longitude: number;
        latitude: number;
        zoom: number;
    };
    onLoad?: (event: { target: any }) => void;
};

const useResizeContainer = (containerRef: RefObject<HTMLDivElement>, mapId: string) => {
    const size = useResizeObserver(containerRef);
    const { [mapId]: map } = useMap();

    useEffect(() => {
        if (size?.width && size.height && map) {
            map.resize();
        }
    }, [map, size?.height, size?.width, mapId]);
};

const MapInner: FC<MapBaseProps & { mapId: string }> = ({ children, mapId, initialViewState, onLoad }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    useResizeContainer(containerRef, mapId);

    return (
        <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
            <ReactMap
                keyboard={false}
                onLoad={onLoad}
                id={mapId}
                mapboxAccessToken={MAPBOX_TOKEN}
                maxPitch={60}
                mapStyle='mapbox://styles/roadgnar/clfwqfhu0000u01mr2x28rxnb'
                reuseMaps
                initialViewState={initialViewState}
            >
                <ScaleControl unit='imperial' position='bottom-right' maxWidth={300} />
                <NavigationControl />
                {children}
            </ReactMap>
        </div>
    );
};

export const MapBase: FC<MapBaseProps> = ({ children, mapId = 'shared-map', initialViewState, onLoad }) => {
    return (
        <MapProvider>
            <MapInner mapId={mapId} initialViewState={initialViewState} onLoad={onLoad}>
                {children}
            </MapInner>
        </MapProvider>
    );
};

